import { Component, ElementRef, EventEmitter, forwardRef, Input, Optional, Output, ViewChild } from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { IonInput } from '@ionic/angular';

@Component({
  selector: 'mable-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
  providers: [
    {
      /*eslint-disable */
      // to fix @typescript-eslint/no-use-before-define
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateComponent),
      multi: true,
      /* eslint-enable */
    },
  ],
})
export class DateComponent implements ControlValueAccessor {
  /** Allows for manually setting on the focus on the ion-input */
  @ViewChild(IonInput, { static: true }) ionInputElement: IonInput;

  /** Allows for manually setting on the focus on the calendarButton */
  @ViewChild('calendarButton', { read: ElementRef, static: false }) calendarButton: ElementRef;

  /** The form control for which the selected date can be set */
  @Input() formControl: UntypedFormControl;

  /** Name of the form control for which the selected date value can be set */
  @Input() formControlName: string;

  /** Display date format */
  @Input() dateDisplayFormat = 'dd/MM/yyyy';

  /** Minimum date allowed */
  @Input() min;

  /** Maximum date allowed */
  @Input() max;

  /** Allows exact years to display, e.g. yearValues="2008,2012,2020,2024" */
  @Input() yearValues;

  /** Show button to toggle calendar modal */
  @Input() showToggleButton = false;

  /** The id of the error message to describe the input. */
  @Input() ariaDescribedBy = 'errorMessage';

  /** The aria label to attach to the time picker input. */
  @Input() ariaLabel;

  /** The id of the element to label the input. */
  @Input() ariaLabelledBy;

  /** Event emitter for date input change */
  @Output() ionChange = new EventEmitter();

  /** Event emitter for date input change */
  @Output() ionInput = new EventEmitter();

  /** DataTestId to pass to the ion-datetime modal */
  @Input() modalDataTestId = 'mable-date';

  isModalOpen = false;

  datetimeControl = new UntypedFormControl();

  // To add a different Id to each date component.
  instanceId = `date-${dateIds++}`;

  /**
   * Flagger to indicate whether the focus on the input is triggered manually after closing the calendar modal.
   * This is used to prevent an infinite loop of the calendar re-opening.
   */
  restoreFocusOnDismiss = false;

  /**
   * Flagger to indicate if the calendar modal was triggered by the calendar button.
   * This is used to restore the focus on the button once the calendar modal is dismissed.
   */
  triggeredByButton = false;

  /**
   *  get hold of FormControl instance no matter formControl or formControlName is given.
   * If formControlName is given, then this.controlContainer.control is the parent FormGroup (or FormArray) instance.
   */
  get control() {
    const control = (this.formControl || this.controlContainer?.control.get(this.formControlName)) as FormControl;

    if (control?.value === '') {
      control.setValue(null);
    }

    return control;
  }

  set value(val) {
    // this value is updated by programmatic changes
    // if (val !== undefined && this.val !== val) {
    this.val = val;
    this.onChange(val);
    this.onTouch();
    // }
  }

  val: any; // this is the updated value that the class accesses

  // ControlContainer made optional for the component to work with ngModel.
  constructor(@Optional() private controlContainer: ControlContainer) {}

  // Function to call when the selected options change.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => {
    // console.log(value);
  };

  //   // Function to call when the input is touched (when a star is clicked).
  onTouch = () => {};

  // this method sets the value programmatically
  writeValue(value: any) {
    this.value = value;
    this.ionChange.emit({ detail: { value } });
    this.ionInput.emit({ detail: { value } });
  }

  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  // upon touching the element, this method gets triggered
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  /**
   * Sets modal isOpen state. The isOpen property on ion-modal allows developers to control the presentation state of
   * the modal from their application state.
   *
   * When the modal is dismissed, the focus will be restored on the trigger source, i.e. input if it was triggered by
   * the input, otherwise the calendar button. If the focus is restored to the input, prevent the modal from opening.
   *
   * @param isOpen - This means when isOpen is set to true the modal will be presented and when isOpen is set to false the modal will be dismissed.
   * @param triggeredByButton - Whether the picker is triggered by the calender button.
   */
  setOpen(isOpen: boolean, triggeredByButton = false): void {
    // prevent the calendar modal from re-opening if the focus is manually triggered by the dismissal of the calendar
    if (this.restoreFocusOnDismiss) {
      this.restoreFocusOnDismiss = false;
      return;
    }

    if (!isOpen) {
      if (this.triggeredByButton) {
        this.calendarButton?.nativeElement?.lastChild?.focus();
      } else {
        this.restoreFocusOnDismiss = true;
        this.ionInputElement.setFocus();
      }
    } else {
      this.triggeredByButton = triggeredByButton;
    }
    this.isModalOpen = isOpen;
  }
}

// since we're assigning IDs to elements and using them, we need to give each instance of the date a unique id
let dateIds = 0;
